import * as React from 'react';
import { Button, Input, FormGroup, Label, ButtonGroup } from 'reactstrap';
import { DropdownComponent, OnChangeEvent } from '../../../dropdown/component';
import { DIMENSION_PRESETS } from '../types';
import { TRexIcon } from '../../../icons/t-rex';
import { UserImagePreference } from '../../../settings-editor/ai-image-preferences/preferences';
import * as ImageConstants from '../../../settings-editor/ai-image-preferences/constants';

interface AiGenerateTabProps {
  prompt: string;
  selectedDimensions: { width: number; height: number; label: string };
  isGenerating: boolean;
  generatedImages: { url: string; width: number; height: number; imageId?: string; timestamp: number }[];
  selectedGeneratedImageIndex: number;
  userPreferences?: UserImagePreference[];
  selectedPreference?: UserImagePreference;
  onPromptChange: (prompt: string) => void;
  onDimensionsChange: (dimensions: { width: number; height: number; label: string }) => void;
  onPreferenceChange?: (preference: UserImagePreference) => void;
  onCustomOptionsChange?: (options: CustomOptions) => void;
  onGeneratedImageSelect?: (index: number) => void;
  onGenerate: () => void;
}

interface CustomOptions {
  style: any;
  quality: any;
  subjectFocus: any;
  colorScheme: any;
  mood: any;
  complexity: any;
  artStyle: any;
  lighting: any;
}

export const AiGenerateTab: React.FC<AiGenerateTabProps> = ({
  prompt,
  selectedDimensions,
  isGenerating,
  generatedImages,
  selectedGeneratedImageIndex,
  userPreferences,
  selectedPreference,
  onPromptChange,
  onDimensionsChange,
  onPreferenceChange,
  onCustomOptionsChange,
  onGeneratedImageSelect,
  onGenerate
}) => {
  const [enhancedPrompt, setEnhancedPrompt] = React.useState(prompt);
  const [customOptions, setCustomOptions] = React.useState<CustomOptions>({
    style: ImageConstants.ImageStyleOptions[0],
    quality: ImageConstants.ImageQualityOptions[1],
    subjectFocus: ImageConstants.SubjectFocusOptions[5],
    colorScheme: ImageConstants.ColorSchemeOptions[7],
    mood: ImageConstants.MoodOptions[0],
    complexity: ImageConstants.ComplexityOptions[1],
    artStyle: ImageConstants.ArtStyleOptions[0],
    lighting: ImageConstants.LightingOptions[0],
  });

  React.useEffect(() => {
    setEnhancedPrompt(prompt);
  }, [prompt]);


  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPrompt = e.target.value;
    setEnhancedPrompt(newPrompt);
    onPromptChange(newPrompt);
  };

  const enhancePrompt = () => {
    if (enhancedPrompt.length > 0) {
      const enhanced = `Create a professional, high-quality image for: ${enhancedPrompt}. Make it visually appealing and engaging.`;
      setEnhancedPrompt(enhanced);
      onPromptChange(enhanced);
    }
  };

  const clearPrompt = () => {
    setEnhancedPrompt('');
    onPromptChange('');
  };

  const handleCustomOptionChange = (optionKey: keyof CustomOptions, event: OnChangeEvent) => {
    const newOptions = {
      ...customOptions,
      [optionKey]: event.selected
    };
    setCustomOptions(newOptions);
    
    // Notify parent modal about custom options changes
    if (onCustomOptionsChange) {
      onCustomOptionsChange(newOptions);
    }
  };

  const handleDimensionSelect = (preset: { width: number; height: number; label: string }) => {
    onDimensionsChange(preset);
  };


  const isCustomSelected = selectedPreference && selectedPreference.value === 'custom';

  if (isGenerating) {
    return (
      <div className="ai-generate-tab">
        <div className="ai-generating">
          <div className="generating-animation">
            <TRexIcon svgwidth="64" svgheight="64" className="trex-spin" />
          </div>
          <h3>Generating your image...</h3>
          <p className="text-muted">This may take a few moments</p>
          <div className="generating-dots">
            <span>●</span>
            <span>●</span>
            <span>●</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-generate-tab">
      <div className="ai-header">
        <div className="ai-title">
          <TRexIcon svgwidth="32" svgheight="32" className="mr-2" />
          <div>
            <h5>Generate with AI</h5>
            <p className="text-muted">Describe the image you want to create</p>
          </div>
        </div>
      </div>

      <FormGroup>
        <Label for="aiPrompt">Image Description</Label>
        <div className="prompt-input-container">
          <Input
            type="text"
            id="aiPrompt"
            placeholder="Describe what you want to see in the image..."
            value={enhancedPrompt}
            onChange={handlePromptChange}
            className="prompt-input"
            disabled={isGenerating}
          />
          <div className="prompt-actions">
            <Button
              color="link"
              size="sm"
              onClick={enhancePrompt}
              disabled={!enhancedPrompt.trim() || isGenerating}
              title="Enhance prompt for better results"
            >
              ✨ Enhance
            </Button>
            <Button
              color="link"
              size="sm"
              onClick={clearPrompt}
              disabled={!enhancedPrompt.trim() || isGenerating}
              title="Clear prompt"
            >
              🗑️ Clear
            </Button>
          </div>
        </div>
        <small className="form-text text-muted">
          Optional: Add additional description to enhance the generated image based on article title and content
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Image Dimensions</Label>
        <ButtonGroup className="dimension-selector">
          {DIMENSION_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              color={selectedDimensions.label === preset.label ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => handleDimensionSelect(preset)}
              disabled={isGenerating}
            >
              {preset.label}
            </Button>
          ))}
        </ButtonGroup>
        
        <small className="form-text text-muted">
          Selected: {selectedDimensions.width} × {selectedDimensions.height}
        </small>
      </FormGroup>

      {userPreferences && userPreferences.length > 0 && (
        <FormGroup>
          <Label>AI Image Preferences</Label>
          <select 
            className="form-control"
            value={selectedPreference ? selectedPreference.value : 'custom'}
            onChange={(e) => {
              if (onPreferenceChange) {
                const preference = userPreferences.find(p => p.value === e.target.value)
                if (preference) {
                  onPreferenceChange(preference)
                } else {
                  // If preference not found, it might be custom - create custom preference
                  onPreferenceChange({
                    key: 'custom',
                    value: 'custom',
                    description: 'Custom',
                    options: null,
                    is_default: false
                  })
                }
              }
            }}
            disabled={isGenerating}
          >
            {userPreferences.map(preference => (
              <option key={preference.value} value={preference.value}>
                {preference.description}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            Choose a saved preference or select "Custom" for manual settings
          </small>

          {selectedPreference && selectedPreference.options && selectedPreference.value !== 'custom' && (
            <div className="preferences-display mt-2">
              <div className="preference-tags">
                <span className="preference-tag">Style: {selectedPreference.options.style || 'Default'}</span>
                <span className="preference-tag">Quality: {selectedPreference.options.quality || 'Default'}</span>
                <span className="preference-tag">Mood: {selectedPreference.options.mood || 'Default'}</span>
                <span className="preference-tag">Colors: {selectedPreference.options.colorScheme || 'Default'}</span>
              </div>
            </div>
          )}

          {isCustomSelected && (
            <div className="custom-options-grid mt-3">
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Image Style</Label>
                    <DropdownComponent
                      data={ImageConstants.ImageStyleOptions}
                      selected={customOptions.style.key}
                      onChange={(e) => handleCustomOptionChange('style', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Image Quality</Label>
                    <DropdownComponent
                      data={ImageConstants.ImageQualityOptions}
                      selected={customOptions.quality.key}
                      onChange={(e) => handleCustomOptionChange('quality', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Subject Focus</Label>
                    <DropdownComponent
                      data={ImageConstants.SubjectFocusOptions}
                      selected={customOptions.subjectFocus.key}
                      onChange={(e) => handleCustomOptionChange('subjectFocus', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Color Scheme</Label>
                    <DropdownComponent
                      data={ImageConstants.ColorSchemeOptions}
                      selected={customOptions.colorScheme.key}
                      onChange={(e) => handleCustomOptionChange('colorScheme', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Mood</Label>
                    <DropdownComponent
                      data={ImageConstants.MoodOptions}
                      selected={customOptions.mood.key}
                      onChange={(e) => handleCustomOptionChange('mood', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Complexity</Label>
                    <DropdownComponent
                      data={ImageConstants.ComplexityOptions}
                      selected={customOptions.complexity.key}
                      onChange={(e) => handleCustomOptionChange('complexity', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Art Style</Label>
                    <DropdownComponent
                      data={ImageConstants.ArtStyleOptions}
                      selected={customOptions.artStyle.key}
                      onChange={(e) => handleCustomOptionChange('artStyle', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>

                <div className="col-md-6">
                  <FormGroup>
                    <Label>Lighting</Label>
                    <DropdownComponent
                      data={ImageConstants.LightingOptions}
                      selected={customOptions.lighting.key}
                      onChange={(e) => handleCustomOptionChange('lighting', e)}
                      disabled={isGenerating}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          )}
        </FormGroup>
      )}

      {generatedImages.length > 0 && (
        <div className="ai-generated-results">
          <h6>Generated Images ({generatedImages.length}):</h6>
          <div className="generated-images-grid">
            {generatedImages.map((image, index) => (
              <div 
                key={image.timestamp} 
                className={`generated-image-item ${index === selectedGeneratedImageIndex ? 'selected' : ''}`}
                onClick={() => onGeneratedImageSelect && onGeneratedImageSelect(index)}
                style={{ cursor: 'pointer', border: index === selectedGeneratedImageIndex ? '3px solid #007bff' : '1px solid #ddd', borderRadius: '8px', padding: '8px', margin: '8px 0' }}
              >
                <img 
                  src={image.url} 
                  alt={`AI Generated ${index + 1}`} 
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px', display: 'block', margin: '0 auto' }}
                />
                <div className="generated-image-info mt-2">
                  <p className="text-muted mb-1">
                    Image {index + 1} - {image.width} × {image.height}
                  </p>
                  {index === selectedGeneratedImageIndex && (
                    <div className="selected-indicator">
                      <span className="badge badge-primary">Selected</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {selectedGeneratedImageIndex >= 0 && generatedImages[selectedGeneratedImageIndex] && (
            <p className="text-muted">
              Click on any image above to select it for use.
            </p>
          )}
        </div>
      )}

      <div className="ai-actions">
        <Button
          color="primary"
          size="lg"
          onClick={onGenerate}
          disabled={isGenerating}
          className="generate-button"
        >
          <TRexIcon svgwidth="24" svgheight="24" className="mr-2" />
          {generatedImages.length > 0 ? 'Generate Another Image' : 'Generate Image'}
        </Button>
      </div>

      <div className="ai-tips">
        <h6>💡 Tips for better AI images:</h6>
        <ul>
          <small>
            <li>Be specific about colors, lighting, and mood</li>
            <li>Mention the style you want (modern, vintage, artistic, etc.)</li>
            <li>Include context about the setting or environment</li>
            <li>Use descriptive adjectives for better results</li>
          </small>
        </ul>
      </div>
    </div>
  );
};